import { inject } from '@angular/core';
import { GuardResult, MaybeAsync, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { AUTH_ACTIONS } from '../store/actions/auth.actions';
import { AUTH_SELECTORS } from '../store/selectors/auth.selectors';
import { ApplicationService } from '../services/application.service';
import { IRootState } from '../store/reducers/root.reducer';

export const authGuard = (): MaybeAsync<GuardResult> => {
  const store = inject(Store) as Store<IRootState>;
  const applicationService = inject(ApplicationService);
  const router: Router = inject(Router);

  return store.select(AUTH_SELECTORS.discoveryDocumentLoaded).pipe(
    filter((loaded) => loaded),
    switchMap(() => store.select(AUTH_SELECTORS.isAuthenticated)),
    authHandler(applicationService, router, store),
  );
};

const authHandler = (
  applicationService: ApplicationService,
  router: Router,
  store: Store,
): ((source: Observable<boolean>) => Observable<boolean>) => {
  return (source: Observable<boolean>): Observable<boolean> =>
    source.pipe(
      tap((isAuthenticated) => {
        if (!isAuthenticated) {
          unauthorizedHandler(applicationService, store);
        } else {
          redirectToLastActiveUrl(applicationService, router);
        }
      }),
    );
};

const redirectToLastActiveUrl = (applicationService: ApplicationService, router: Router): void => {
  const urlRedirectAfterLogin: string | null = applicationService.getUrlFromStorage();
  if (urlRedirectAfterLogin) {
    applicationService.removeUrlFromStorage();
    void router.navigateByUrl(urlRedirectAfterLogin);
  }
};

const unauthorizedHandler = (applicationService: ApplicationService, store: Store): void => {
  applicationService.saveActiveUrlToStorage();
  store.dispatch(AUTH_ACTIONS.login());
};
